// 持卡人 WebApp：营销 + 登录 + Dashboard
// URL 语言前缀：/{lang}/cardholder/* · /{lang}/login · /{lang}（lang = zh | en）
import { Suspense, useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { ToastProvider } from '@/components/Toast';
import ErrorBoundary from '@/components/ErrorBoundary';
import OfflineBanner from '@/components/OfflineBanner';
import { setAuthFailureHandler } from '@/api/client';
import RequireAuth from '@/auth/RequireAuth';
import RequireSetup from '@/auth/RequireSetup';
import LandingPage from '@/pages/landing';
import LoginPage from '@/pages/auth';
import RegisterInvitePage from '@/pages/auth/RegisterInvitePage';
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage';
import LocaleLayout from '@/i18n/LocaleLayout';
import {
  getPreferredLang,
  localePath,
  parseLangFromPath,
} from '@/i18n/localePath';
import { lazyRoute } from '@/lib/lazyRoute';

const DashboardShell = lazyRoute(() => import('@/pages/dashboard'));
const PortalLoginPage = lazyRoute(() => import('@/pages/portal-login'));
const DepositPage = lazyRoute(() => import('@/pages/account/deposit'));
const RewardsPage = lazyRoute(() => import('@/pages/rewards'));
const MessagesPage = lazyRoute(() => import('@/pages/messages'));
const KycPage = lazyRoute(() => import('@/pages/kyc'));
const TransactionsPage = lazyRoute(() => import('@/pages/account/transactions'));
const MerchantCreditDetailPage = lazyRoute(() => import('@/pages/account/merchant-credits'));
const CardDetailPage = lazyRoute(() => import('@/pages/cards/detail'));
const CardActivatePage = lazyRoute(() => import('@/pages/cards/activate'));
const LivenessPage = lazyRoute(() => import('@/pages/liveness'));
const ChainTrackingPage = lazyRoute(() => import('@/pages/account/deposit-tracking'));
const ProfileMePage = lazyRoute(() => import('@/pages/profile/me'));
const ProfileSecurityPage = lazyRoute(() => import('@/pages/profile/security'));
const TwoFaPage = lazyRoute(() => import('@/pages/profile/security/2fa'));
const PaymentPasswordPage = lazyRoute(() => import('@/pages/profile/security/payment-password'));
const PreferencesPage = lazyRoute(() => import('@/pages/profile/preferences'));
const HelpPage = lazyRoute(() => import('@/pages/profile/help'));

function Fallback() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-canvas text-slate-400">
      <div className="w-10 h-10 rounded-2xl bg-brand-gradient animate-pulse" />
      <span className="text-sm font-medium">Loading…</span>
    </div>
  );
}

/** 无语言前缀的旧路径 → /{preferredLang}/... */
function RedirectLegacyToLocale() {
  const location = useLocation();
  const target = `${localePath(location.pathname, getPreferredLang())}${location.search}${location.hash}`;
  return <Navigate to={target} replace={true} />;
}

/** 路由内部：注册 401 鉴权失败回调 → /{lang}/login */
function AppRoutes() {
  const navigate = useNavigate();
  useEffect(() => {
    setAuthFailureHandler(() => {
      const lang = parseLangFromPath(window.location.pathname) ?? getPreferredLang();
      navigate(localePath('/login', lang));
    });
    return () => setAuthFailureHandler(null);
  }, [navigate]);

  return (
    <Routes>
      {/* 根路径 → 偏好语言首页 */}
      <Route path="/" element={<Navigate to={localePath('/', getPreferredLang())} replace={true} />} />

      {/* 兼容无语言前缀的旧链接 */}
      <Route path="/login" element={<RedirectLegacyToLocale />} />
      <Route path="/cardholder/*" element={<RedirectLegacyToLocale />} />
      <Route path="/dashboard" element={<Navigate to={localePath('/cardholder/dashboard', getPreferredLang())} replace={true} />} />

      {/* 免登录活体旧链接 → /{lang}/liveness/:token */}
      <Route path="/liveness/:token" element={<RedirectLegacyToLocale />} />

      {/* 运营代登录落地页（2026-10-23 · spec §13.9）：语言无关顶层路由（不挂 /:lang 前缀，
          票据 60s TTL 内不因语言协商跳转浪费时间；成功后由页面带语言跳 /{lang}/cardholder/dashboard） */}
      <Route path="/portal-login" element={<PortalLoginPage />} />

      {/* /:lang/... */}
      <Route path="/:lang" element={<LocaleLayout />}>
        <Route index element={<LandingPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterInvitePage />} />
        <Route path="verify-email" element={<VerifyEmailPage />} />
        <Route path="liveness/:token" element={<LivenessPage />} />
        <Route path="cardholder/login" element={<Navigate to="../login" replace={true} />} />

        <Route element={<RequireAuth />}>
          <Route element={<RequireSetup />}>
          <Route path="cardholder" element={<Navigate to="dashboard" relative="path" replace={true} />} />
          <Route path="cardholder/dashboard" element={<DashboardShell />} />
          <Route path="cardholder/account" element={<DashboardShell />} />
          <Route path="cardholder/cards" element={<DashboardShell />} />
          <Route path="cardholder/cards/:cardId" element={<CardDetailPage />} />
          <Route path="cardholder/cards/:cardId/activate" element={<CardActivatePage />} />
          <Route path="cardholder/account/deposit" element={<DepositPage />} />
          {/* 链上确认追踪页保留（用户 2026-09-27 裁定）；同批下线的充值列表页与诊断页路由已移除 */}
          <Route path="cardholder/account/deposits/:depositId" element={<ChainTrackingPage />} />
          <Route path="cardholder/account/rewards" element={<RewardsPage />} />
          <Route path="cardholder/account/messages" element={<MessagesPage />} />
          <Route path="cardholder/kyc" element={<KycPage />} />
          <Route path="cardholder/account/transactions" element={<TransactionsPage />} />
          <Route
            path="cardholder/account/merchant-credits/:creditId"
            element={<MerchantCreditDetailPage />}
          />
          <Route path="cardholder/profile/me" element={<ProfileMePage />} />
          <Route path="cardholder/profile/security" element={<ProfileSecurityPage />} />
          <Route path="cardholder/profile/security/2fa" element={<TwoFaPage />} />
          {/* S10b PAGE-CHD-PRF-009：支付密码管理（注册后 RequireSetup 强制引导直达） */}
          <Route
            path="cardholder/profile/security/payment-password"
            element={<PaymentPasswordPage />}
          />
          <Route path="cardholder/profile/preferences" element={<PreferencesPage />} />
          <Route path="cardholder/profile/help" element={<HelpPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to={localePath('/', getPreferredLang())} replace={true} />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <OfflineBanner />
        <BrowserRouter>
          <Suspense fallback={<Fallback />}>
            <AppRoutes />
          </Suspense>
        </BrowserRouter>
      </ToastProvider>
    </ErrorBoundary>
  );
}
